import { App } from 'vue'
import { Router } from 'vue-router'

import Sentry, {
  getDefaultIntegrations,
  groupWithCustomNamedIssue,
} from '@collector/shared-plugin-sentry-vue'
import { SportsApiEndpointsWithoutLoginPattern } from '@collector/sportsapi-types'
import { Env } from '@desktop/envs'

export const initSentry = (
  app: App<Element>,
  router: Router,
): ReturnType<typeof Sentry.init> => {
  const client = Sentry.init({
    app,
    enabled: Env.VITE_SENTRY_ENABLED === 'true',
    environment: Env.VITE_SENTRY_ENVIRONMENT,
    dsn: Env.VITE_SENTRY_DSN,
    release: Env.VITE_RELEASE_NAME,
    integrations: getDefaultIntegrations({
      router,
      failedRequestStatusCodes: [400, [402, 599]],
      failedRequestTargets: [SportsApiEndpointsWithoutLoginPattern],
      networkDetailAllowUrlsPattern: [SportsApiEndpointsWithoutLoginPattern],
    }),
    tracesSampleRate: 1,
    tracePropagationTargets: [import.meta.env.VITE_QUEUE_GATEWAY_URL],
    attachErrorHandler: true,

    beforeSend: (event) => {
      const isPostToIncidentsQueue409 =
        event?.request?.method === 'POST' &&
        event?.request?.url &&
        new URL(event?.request?.url).pathname.split('/').pop() ===
          'incidents-queue' &&
        event?.contexts?.response?.status_code === 409

      // drop exception
      if (isPostToIncidentsQueue409) {
        return null
      }

      event.transaction = window.location.pathname
      event.extra = {
        ...event.extra,
        title: document.title,
      }

      groupWithCustomNamedIssue(
        event as Parameters<typeof groupWithCustomNamedIssue>[0],
      )

      return event
    },
    urlsWithTrackedPayload: SportsApiEndpointsWithoutLoginPattern,
  } as Parameters<typeof Sentry.init>[0])

  Sentry.getGlobalScope().setTag('releaseType', Env.VITE_RELEASE_TYPE)

  return client
}
